import * as React from 'react';

type Props = Readonly<{ children: React.ReactNode }>;
type State = { hasError: boolean };

/*
	Catches render-time crashes anywhere in the checkout tree so a JavaScript
	error shows a plain "something went wrong" page instead of a blank screen.
	Two such crashes have happened on this project (an unpriced offer, a stray
	spinner), and a blank page mid-checkout reads as "the site is broken".

	The message is deliberately neutral about payment: the boundary can trip at
	any step, including after the card has been taken, so it must not claim the
	customer has or hasn't been charged. The session is persisted server-side, so
	a refresh returns them to the step they were on.
*/
class ErrorBoundary extends React.Component<Props, State> {
	state: State = { hasError: false };

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	componentDidCatch(error: unknown, info: unknown): void {
		console.error('Checkout render error:', error, info);
	}

	render(): React.ReactNode {
		if (!this.state.hasError) {
			return this.props.children;
		}

		return (
			<div className="max-w-screen-xl mx-auto px-6 sm:px-8 text-center my-16">
				<img src="/images/logo/logo.svg" alt="Feelfit" className="mx-auto mb-8"/>
				<h3>SOMETHING WENT WRONG</h3>
				<p className="mb-8">
					Sorry, this page ran into a problem. Your progress is saved — please refresh to
					continue where you left off. If it keeps happening, please contact the club.
				</p>
				<button
					className="!mb-0 !inline-flex items-center justify-center"
					onClick={() => globalThis.location.reload()}
				>
					REFRESH THE PAGE
				</button>
			</div>
		);
	}
}

export default ErrorBoundary;
